import type { BdrLead, BdrCloser, BdrPartner, BdrSdr } from '@/types'

type BdrUserRow = { id?: string; name?: string; nome?: string; email?: string }

/** Normaliza GET /bdr/closers, /bdr/sdrs, /bdr/partners (array direto ou envelope). */
export function parseBdrUserList<T extends BdrUserRow>(payload: unknown): T[] {
  const raw = (payload as { data?: unknown })?.data ?? payload
  const list = Array.isArray(raw) ? raw : []
  return list
    .filter((row): row is BdrUserRow => row != null && typeof row === 'object')
    .map((row) => ({
      id: String(row.id ?? ''),
      name: String(row.name ?? row.nome ?? '').trim(),
      email: row.email,
    }))
    .filter((row) => row.id && row.name) as T[]
}

export function parseBdrClosers(payload: unknown): BdrCloser[] {
  return parseBdrUserList<BdrCloser>(payload)
}

export function parseBdrSdrs(payload: unknown): BdrSdr[] {
  return parseBdrUserList<BdrSdr>(payload)
}

export function parseBdrPartners(payload: unknown): BdrPartner[] {
  const raw = (payload as { data?: unknown })?.data ?? payload
  const list = Array.isArray(raw) ? raw : []
  return list
    .filter((row): row is { id?: string; nome?: string; name?: string } => row != null && typeof row === 'object')
    .map((row) => ({
      id: String(row.id ?? ''),
      name: String(row.nome ?? row.name ?? '').trim(),
    }))
    .filter((row) => row.id && row.name)
}

/** Resposta paginada de /bdr/queue e /bdr/nurturing ({ success, data: { leads, total, ... } }). */
export function parseBdrPaginatedResponse(responseData: unknown): {
  items: BdrLead[]
  total: number
  hasMore: boolean
  page: number
  limit: number
} {
  const root = (responseData ?? {}) as unknown as Record<string, unknown>
  const payload = (root.data ?? root) as unknown as Record<string, unknown>

  let items: BdrLead[] = []
  if (Array.isArray(payload.leads)) {
    items = payload.leads as BdrLead[]
  } else if (Array.isArray(root.leads)) {
    items = root.leads as BdrLead[]
  } else if (Array.isArray(payload)) {
    items = payload as BdrLead[]
  }

  const page = Number(payload.page ?? root.page ?? 1)
  const limit = Number(payload.limit ?? root.limit ?? 30)
  const total = Number(payload.total ?? root.total ?? items.length)
  const hasMore = Boolean(payload.hasMore ?? root.hasMore ?? page * limit < total)

  return { items, total, hasMore, page, limit }
}
